import axios from 'axios'
import { API_POLICY_PRIMARY, resolveApiRequestPolicy } from '@/config/requestPolicies'
import { emitSubscriptionRequired, isSubscriptionRequired } from '@/utils/subscriptionAccess'

axios.defaults.validateStatus = false

function normalizeBaseURL(value, fallback = '') {
  const resolved = (value || fallback || '').trim()
  if (!resolved) return ''
  return resolved.endsWith('/') ? resolved : `${resolved}/`
}

function trimLeadingSlash(value) {
  return String(value || '').replace(/^\/+/, '')
}

class API {
  constructor(config) {
    const resolvedConfig = typeof config === 'string'
      ? { primaryBaseURL: config }
      : (config || {})

    this.primaryBaseURL = normalizeBaseURL(resolvedConfig.primaryBaseURL, '/api')
    this.fallbackBaseURL = normalizeBaseURL(resolvedConfig.fallbackBaseURL)
    this.activeBaseURL = this.primaryBaseURL
    this.pingPath = resolvedConfig.pingPath || 'api/version'
    this.pingTimeout = resolvedConfig.pingTimeout || 3000

    // Compatibility alias for places that still read api.baseURL directly.
    this.baseURL = this.activeBaseURL
    this.token = null
  }

  setToken(token) {
    this.token = token
  }

  getAuthHeaders(extraHeaders = {}) {
    const headers = { ...extraHeaders }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    return headers
  }

  buildRequestUrl(baseURL, url) {
    return `${normalizeBaseURL(baseURL, this.primaryBaseURL)}${trimLeadingSlash(url)}`
  }

  buildUrl(url, baseURL = this.activeBaseURL) {
    return this.buildRequestUrl(baseURL, url)
  }

  setActiveBaseURL(baseURL) {
    this.activeBaseURL = normalizeBaseURL(baseURL, this.primaryBaseURL)
    this.baseURL = this.activeBaseURL
  }

  getActiveBaseURL() {
    return this.activeBaseURL
  }

  getCandidateBaseURLs(policy = 'primary') {
    const candidates = [this.activeBaseURL]

    if (
      policy === 'hybrid'
      && this.fallbackBaseURL
      && this.fallbackBaseURL !== this.activeBaseURL
    ) {
      candidates.push(this.fallbackBaseURL)
    }

    return candidates
  }

  async pingBaseURL(baseURL) {
    const response = await axios({
      method: 'get',
      url: this.buildRequestUrl(baseURL, this.pingPath),
      timeout: this.pingTimeout,
      headers: this.getAuthHeaders(),
      params: { t: Date.now() }
    })

    return Boolean(response)
  }

  async initializeBaseURL() {
    try {
      await this.pingBaseURL(this.primaryBaseURL)
      this.setActiveBaseURL(this.primaryBaseURL)
      console.log('[API] Primary API is available:', this.primaryBaseURL)
      return this.activeBaseURL
    } catch (primaryError) {
      if (!this.fallbackBaseURL || this.fallbackBaseURL === this.primaryBaseURL) {
        console.warn('[API] Primary API ping failed and fallback is not configured:', primaryError)
        return this.activeBaseURL
      }

      try {
        await this.pingBaseURL(this.fallbackBaseURL)
        this.setActiveBaseURL(this.fallbackBaseURL)
        console.warn('[API] Primary API is unavailable, using fallback:', this.fallbackBaseURL)
        return this.activeBaseURL
      } catch (fallbackError) {
        console.warn('[API] Both primary and fallback API pings failed, keeping primary API:', {
          primaryError,
          fallbackError
        })
        this.setActiveBaseURL(this.primaryBaseURL)
        return this.activeBaseURL
      }
    }
  }

  shouldTryFallback(error, policy, candidateIndex, candidatesLength) {
    return policy === 'hybrid'
      && candidateIndex < candidatesLength - 1
      && !error?.response
  }

  async request(method, url, options = {}) {
    const {
      params = {},
      data,
      headers = {},
      timeout,
      responseType,
      onUploadProgress,
      policy: explicitPolicy
    } = options

    const policy = explicitPolicy || resolveApiRequestPolicy(method, url)

    const candidates = this.getCandidateBaseURLs(policy)
    let lastError = null
    let lastResponseData = null

    for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
      const baseURL = candidates[candidateIndex]

      try {
        const response = await axios({
          method,
          url: this.buildRequestUrl(baseURL, url),
          params,
          data,
          headers: this.getAuthHeaders(headers),
          timeout,
          responseType,
          onUploadProgress
        })

        // For client-side errors we do not jump to fallback: backend answered us.
        if (response.status < 500 || candidateIndex === candidates.length - 1) {
          if (isSubscriptionRequired(response.data)) {
            emitSubscriptionRequired({ response: response.data, url })
          }
          return response.data
        }

        lastResponseData = response.data
      } catch (error) {
        lastError = error

        if (!this.shouldTryFallback(error, policy, candidateIndex, candidates.length)) {
          if (error.response?.data) {
            if (isSubscriptionRequired(error.response.data)) {
              emitSubscriptionRequired({ response: error.response.data, url })
            }
            return error.response.data
          }

          throw error
        }
      }
    }

    if (lastResponseData) {
      if (isSubscriptionRequired(lastResponseData)) {
        emitSubscriptionRequired({ response: lastResponseData, url })
      }
      return lastResponseData
    }

    if (lastError?.response?.data) {
      if (isSubscriptionRequired(lastError.response.data)) {
        emitSubscriptionRequired({ response: lastError.response.data, url })
      }
      return lastError.response.data
    }

    if (lastError) {
      throw lastError
    }

    return null
  }

  async get(url, params = {}, options = {}) {
    return this.request('get', url, { ...options, params })
  }

  async post(url, data, options = {}) {
    return this.request('post', url, {
      ...options,
      data,
      timeout: options.timeout || 10000
    })
  }

  async put(url, data, options = {}) {
    return this.request('put', url, { ...options, data })
  }

  async patch(url, data, options = {}) {
    return this.request('patch', url, { ...options, data })
  }

  async delete(url, options = {}) {
    return this.request('delete', url, options)
  }

  async upload(url, file, params = {}, options = {}) {
    const form = new FormData()
    form.append('file', file)

    const response = await this.request('post', url, {
      ...options,
      data: form,
      params,
      headers: options.headers || {},
      timeout: options.timeout || 30000
    })

    if (response && response.success === false) {
      throw new Error(response.error || response.message || 'Upload failed')
    }

    return response
  }
}

export { API, API_POLICY_PRIMARY }
